import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import config from 'constants/config'
import moment from 'moment'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import { rem } from 'polished'
import USPModal from './USPModal'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import TextLink from 'LuxKit/TextLink'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'

const USPContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: minmax(min-content, max-content) minmax(min-content, max-content);
    align-items: flex-start;
  }
`

const USPBodyText = styled(Caption)`
  margin-left: ${rem(24)};
`

interface Props {
  offer: App.Offer;
  checkIn?: string;
  availableForPurchase?: boolean;
}

type ModalTypes = 'bnbl' | 'bestPrice' | 'gifting'

function FlashHotelUSPs(props: Props) {
  const { offer, checkIn, availableForPurchase } = props
  const [modalType, setModalType] = useState<ModalTypes>()
  const [open, setOpen] = useState(false)
  const intlDateFormatter = useIntlDateFormatter()

  const onItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const modalType = e.currentTarget.dataset.type as ModalTypes
    setModalType(modalType)
    setOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  const hasBNBL = offer.hasBuyNowBookLaterPackages && offer.hasBuyNowBookLater && offer.travelToDate && config.accommodation.hasBnbl
  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const bookBy = useMemo(() => moment(offer.bookByDate), [offer.bookByDate])
  const travelUntil = useMemo(() => moment(offer.travelToDate), [offer.travelToDate])

  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag)

  return (
    <>
      <USPContainer>
        {availableForPurchase && <FlashCancellationPolicies
          checkInDate={checkIn}
          propertyTimezoneOffset={offer?.property?.timezoneOffset}
          cancellationPolicy={offer?.lowestPricePackage?.roomRate?.cancellationPolicy}
          isFlashUSP
        />}
        {!offer.bundledWithFlightsOnly && config.bestPriceGuarantee && !offer.disableBestPriceGuarantee && (
          <TextLink
            size="medium"
            colour="highlight-secondary"
            underline={false}
            data-type="bestPrice"
            onClick={onItemClick}
            startIcon={isWePriceMatchEnabled ? <LineTagAltIcon /> : <LineAwardIcon />}
          >
            {isWePriceMatchEnabled ? 'We price match' : 'Best price guarantee'}
          </TextLink>
        )}
        {hasBNBL && <div>
          <TextLink
            size="medium"
            colour="highlight-secondary"
            underline={false}
            data-type="bnbl"
            data-testid="usp-bnbl"
            onClick={onItemClick}
            startIcon={<LineCalendarIcon />}
          >
            Buy now, choose dates later
          </TextLink>
          <USPBodyText colour="neutral-three" variant="medium">
            Travel until {intlDateFormatter(travelUntil, INTL_DMY_CASUAL_FORMAT)}
          </USPBodyText>
        </div>}
        {hasBNBL && isSevenDaysBeforeTravelToDate && !isMemberOnlyOffer &&
          <TextLink
            size="medium"
            colour="highlight-secondary"
            underline={false}
            data-type="gifting"
            onClick={onItemClick}
            startIcon={<LineGiftIcon />}
          >
            Gifting available
          </TextLink>}
      </USPContainer>
      <USPModal open={open} onClose={onClose} modalType={modalType} bookBy={bookBy} travelUntil={travelUntil} />
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  availableForPurchase: isAvailableForPurchase(state, appState),
}), FlashHotelUSPs)
